/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */
import axios from 'axios';
import { getAccessToken } from '@cenozon/indoraptor-user-auth-react-spa';
import { displayText, sessionStorageKey, stringConstant } from '../../constant';
import { decryptData } from '../../components/shared/helper';

const apiUrl = process.env.REACT_APP_API_URL ?? stringConstant.EMPTY_STRING;

export const buildAuthHeaders = (data, extra = {}) => {
   const token = getAccessToken();
   // Send a JSON Content-Type only for plain-object/array payloads. Binary
   // bodies (FormData, Blob, ArrayBuffer, ...) must keep the Content-Type the
   // browser/axios derives for them — an explicit JSON Content-Type makes
   // axios 1.x serialize them to JSON — and bodyless requests need none.
   const proto = data == null ? undefined : Object.getPrototypeOf(data);
   const isJsonPayload = Array.isArray(data) || proto === Object.prototype || proto === null;
   return {
      ...(isJsonPayload ? { 'Content-Type': displayText.APPLICATION_JSON } : {}),
      ...(token ? { Authorization: `${displayText.BEARER} ${token}` } : {}),
      ClientsGuid: `${JSON.parse(decryptData(sessionStorageKey.USER_CURRENT_CLIENT_DETAILS))?.clientsGuid}`,
      ...extra
   };
};

export default {
   // Get all
   downloadBlob: (url) => new Promise((resolve, reject) => {
      axios.get(`${apiUrl}${displayText.API}/${url}`, {
         headers: buildAuthHeaders(),
         responseType: 'blob'
      })
         .then(result => resolve(result))
         .catch(err => reject(err));
   }),

   getAllData: url => new Promise((resolve) => {
      axios
         .get(`${apiUrl}${displayText.API}/${url}`, {
            headers: buildAuthHeaders()
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.message);
         });
   }),

   postData: (url, data) => new Promise((resolve) => {
      axios
         .post(`${apiUrl}${displayText.API}/${url}`, data, {
            headers: buildAuthHeaders(data)
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.response?.data ?? err.message);
         });
   }),

   editData: (url, data) => {
      return new Promise((resolve) => {
         axios
            .put(`${apiUrl}${displayText.API}/${url}`, data, {
               headers: buildAuthHeaders(data)
            })
            .then(result => {
               resolve(result);
            })
            .catch(err => {
               resolve(err.message);
            });
      });
   },

   deleteCall: (url, data) => new Promise((resolve) => {
      axios
         .delete(`${apiUrl}${displayText.API}/${url}`, {
            headers: buildAuthHeaders(data),
            data
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.response || err);
         });
   }),

   getExcelPreview: (url, data) => new Promise((resolve, reject) => {
      axios.post(`${apiUrl}${displayText.API}/${url}`, data, {
         headers: buildAuthHeaders(data)
      })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            reject(err);
         });
   }),

   passwordChangePostCall: (url, data) => new Promise((resolve) => {
      axios.post(`${apiUrl}${displayText.API}/${url}`, data, {
         headers: buildAuthHeaders(data),
      })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.message);
         });
   }),

   deleteFileCall: url => new Promise((resolve, reject) => {
      fetch(`${apiUrl}${displayText.API}/${url}`, {
         headers: buildAuthHeaders(),
         credentials: 'include',
         method: 'POST',
         mode: 'same-origin',
         keepalive: true
      })
         .then(result => {
            resolve(result);
         })
         .catch(e => {
            reject(e);
         });
   }),

   qcDashboardCall: (url, data) => new Promise(async (resolve) => {
      await fetch(`${apiUrl}${displayText.API}/${url}`, {
         // The body below is unconditionally JSON-encoded, so the Content-Type
         // must be unconditionally JSON too — never derived from the payload.
         headers: buildAuthHeaders({}),
         body: JSON.stringify(data),
         credentials: 'include',
         method: 'POST'
      })
      .then(result => {
         resolve(result.json());
      })
      .catch(e => {
         console.log(e);
      });
   }),

   postDataAsyncWithCancelToken: (url, data, newCancelToken, isCancel) => new Promise(async (resolve, reject) => {
      try {
         const result = await axios.post(`${apiUrl}${displayText.API}/${url}`, data, {
            headers: buildAuthHeaders(data),
            cancelToken: newCancelToken,
         });
         resolve(result);
      } catch (error) {
         if (isCancel) {
            reject('user has cancelled the request.');
         }
         resolve(error.response ?? error.message);
      }
   }),

   getAllLookupData: (url, clientId) => new Promise((resolve) => {
      axios
         .get(`${apiUrl}${displayText.API}/${url}`, {
            headers: buildAuthHeaders(null, (clientId) ? { CurrentClientGuid: clientId } : {})
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.message);
         });
   }),

   postLookupData: (url, data, clientId) => new Promise((resolve) => {
      axios
         .post(`${apiUrl}${displayText.API}/${url}`, data, {
            headers: buildAuthHeaders(data, (clientId) ? { CurrentClientGuid: clientId } : {})
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.message);
         });
   }),

   editLookupData: (url, data, clientId) => {
      return new Promise((resolve) => {
         axios
            .put(`${apiUrl}${displayText.API}/${url}`, data, {
               headers: buildAuthHeaders(data, (clientId) ? { CurrentClientGuid: clientId } : {}),
            })
            .then(result => {
               resolve(result);
            })
            .catch(err => {
               resolve(err.message);
            });
      });
   },

   deleteLookupCall: (url, data, clientId) => new Promise((resolve) => {
      axios
         .delete(`${apiUrl}${displayText.API}/${url}`, {
            headers: buildAuthHeaders(data, (clientId) ? { CurrentClientGuid: clientId } : {}),
            data
         })
         .then(result => {
            resolve(result);
         })
         .catch(err => {
            resolve(err.message);
         });
   })
};
